import { ApiError } from "@propertelligent/client-api";
import { NotificationType, PubSub, TopicsEnum } from "./pubsub";

const apiHelper = async (
  apiCall,
  apiNotificationSettings: ApiNotificationSettings,
  ...params
) => {
  let responseData = null;
  let errors = null;
  let statusCode = null;

  try {
    responseData = await apiCall(...params);
    if (apiNotificationSettings.showNotification) {
      // --- CHANGE 1: Process successMessage as a function or string ---
      const message =
        typeof apiNotificationSettings.successMessage === "function"
          ? apiNotificationSettings.successMessage(responseData)
          : apiNotificationSettings.successMessage || "Api completed successfully";

      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: message,
        type: NotificationType.Success,
        title: apiNotificationSettings.title || "Success", // --- CHANGE 2: Pass title ---
        icon: apiNotificationSettings.successIcon,
      });
    }
  } catch (err) {
    var apiError = err as ApiError;

    // --- CHANGE 3: Process failureMessage as a function or string ---
    const failureMessage =
      typeof apiNotificationSettings.failureMessage === "function"
        ? apiNotificationSettings.failureMessage(apiError)
        : apiNotificationSettings.failureMessage;

    if (apiError?.body?.errors) {
      errors = apiError.body.errors;
    }

    const status = apiError?.status ?? (apiError as { response?: { status?: number } })?.response?.status;
    // Expose the real status for every failure, not just 409 (the only case this previously set) --
    // callers that need to distinguish a specific failure (e.g. a genuine 404 vs. a transient 500)
    // can read it off the returned statusCode instead of re-deriving it themselves.
    statusCode = status ?? null;

    if (status === 401) {
      PubSub.publish(TopicsEnum.Logout.toString(), {
        type: NotificationType.Failure,
        message: "Session expired, login again.",
        title: "Session Expired",
        icon: apiNotificationSettings.failureIcon,
      });
      return;
    }

    const bodyErrors = apiError?.body?.errors;
    let errorMessageText = (bodyErrors && typeof bodyErrors === "object" && Object.keys(bodyErrors).length > 0)
      ? bodyErrors[Object.keys(bodyErrors)[0]][0]
      : "";
    let errorMessage;
    switch (status) {
      case 400:
        errorMessage = "Bad Request (400)";
        break;
      case 401:
        // This case is handled above, but kept for logic flow
        errorMessage = "Unauthorized (401)";
        PubSub.publish(TopicsEnum.Logout.toString(), {
          type: NotificationType.Failure,
          message: "Session expired, login again.",
          title: "Session Expired",
          icon: apiNotificationSettings.failureIcon,
        });
        return;
      case 403:
        errorMessage = "User not authorized to perform this action.";
        break;
      case 415:
        errorMessage = "Media Type not Allowed (415)";
        break;
      case 500:
        errorMessage = "Internal Server Error (500)";
        PubSub.publish(TopicsEnum.ServerError.toString(), { // Publish to ServerError topic
          type: NotificationType.Failure,
          message: errorMessageText || failureMessage || errorMessage, // Use resolved message
          title: "Server Error", // --- CHANGE 4: Add title ---
        });
        break;
      case 409:
        // statusCode is already set above (from the more robust `status`, which falls back to
        // apiError.response.status when apiError.status is missing) -- this case needs no extra
        // handling now, just the same fall-through as the others.
        break;
      default:
        errorMessage = `Unexpected Error (${status ?? statusCode})`;
        if (status >= 500 && status < 600) {
          PubSub.publish(TopicsEnum.ServerError.toString(), {
            type: NotificationType.Failure,
            message: errorMessageText || failureMessage || errorMessage,
            title: "Server Error",
          });
        }
        break;
    }
    if (apiNotificationSettings.showNotification) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        message: errorMessageText || failureMessage || errorMessage, // Use resolved message
        title: "Failure", // --- CHANGE 5: Add default failure title ---
        icon: apiNotificationSettings.failureIcon,
      });
    }
  }

  return {
    data: responseData,
    errors,
    statusCode
  };
};

export default apiHelper;

// --- CHANGE 6: Update type definition ---
export type ApiNotificationSettings = {
  successMessage?: string | ((response: any) => string);
  failureMessage?: string | ((error: ApiError) => string);
  showNotification: boolean;
  title?: string;
  successIcon?: React.ReactNode; 
  failureIcon?: React.ReactNode; 
};